/**
 * keyboard-aware.js — Lock the inbox shell to the visual viewport so the
 * header stays anchored at the visible top and the composer stays
 * anchored just above the on-screen keyboard — the WhatsApp-native
 * behavior agents expect.
 *
 * Why this exists:
 *   When iOS Safari's on-screen keyboard appears, the layout viewport
 *   stays at full screen height, but the visual viewport (the visible
 *   area above the keyboard) shrinks. iOS then auto-scrolls the document
 *   to bring the focused <textarea> into view, which drags every
 *   position-fixed element up with it — including the thread header.
 *   The agent sees the entire chat screen "jump up" and sometimes the
 *   focused composer ends up under the keyboard on the first tap (the
 *   "second tap works" symptom Sambit reported).
 *
 *   The fix is to stop trusting the layout viewport entirely. We set
 *   CSS custom properties from window.visualViewport so the shell
 *   container can size itself to the visible region:
 *
 *     --kbd-app-height       → visualViewport.height        (visible height)
 *     --kbd-app-offset-top   → visualViewport.offsetTop     (top inset on iOS pinch/scroll)
 *
 *   `.inbox-screen` consumes these via inbox-v2.css. The container shrinks
 *   when the keyboard appears, the flex column reflows internally — header
 *   stays at the visible top, composer at the visible bottom — and iOS
 *   has nothing to auto-scroll because we also call window.scrollTo(0,0).
 *
 * Cross-browser notes:
 *   - iOS Safari: visualViewport.height shrinks; layout viewport stays.
 *     This is the problem case this helper solves.
 *   - Android Chrome: with interactive-widget=resizes-content (set in the
 *     viewport meta), the layout viewport ALSO shrinks. innerHeight and
 *     visualViewport.height stay in lockstep. Our handler still runs;
 *     the values are just identical to what the layout would give us.
 *   - Desktop: visualViewport.height ≈ innerHeight permanently. No-op.
 */

let _bound = false;

function _apply() {
  const vv = window.visualViewport;
  if (!vv) return;

  const root = document.documentElement;
  // The visible-area height. iOS Safari shrinks this when keyboard opens
  // even though innerHeight stays at full screen. Android Chrome with
  // interactive-widget=resizes-content shrinks innerHeight too, so
  // vv.height ≡ innerHeight there — same value, same result.
  root.style.setProperty('--kbd-app-height', `${vv.height}px`);
  // Offset from the top of the layout viewport. Nonzero on iOS when the
  // user pinch-zooms or scrolls past 0 — we'd otherwise miss the top of
  // the visible region and tuck the header out of sight.
  root.style.setProperty('--kbd-app-offset-top', `${vv.offsetTop}px`);

  // Heuristic "is the on-screen keyboard open?" — true when the visual
  // viewport is meaningfully shorter than the layout viewport. iOS sometimes
  // keeps env(safe-area-inset-bottom) at its closed value (~34px) even when
  // the keyboard is up, which leaves a stale gap above the iOS form
  // accessory bar (^ v Done). Toggling .kbd-open on <html> lets the
  // composer collapse its bottom padding to just the visual floor when the
  // keyboard occupies the safe-area zone, and restore the home-indicator
  // padding when the keyboard goes away.
  const kbdH = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
  root.classList.toggle('kbd-open', kbdH > 100);

  // Undo any iOS auto-scroll. With our `.inbox-screen` re-anchored to
  // the visual viewport, the document doesn't need to scroll for the
  // composer to be visible. Skipping this lets iOS pull the layout up
  // by a few pixels on first focus, which is the "everything moved up"
  // glitch users see.
  if (window.scrollY !== 0 || window.scrollX !== 0) {
    window.scrollTo(0, 0);
  }
}

/**
 * Wire visualViewport events once. Safe to call from multiple boot paths
 * — repeat calls are no-ops. Initial values are written synchronously so
 * the first paint after boot already has correct sizing variables.
 */
export function initKeyboardAware() {
  if (_bound) return;
  if (typeof window === 'undefined') return;
  const vv = window.visualViewport;
  if (!vv) {
    // Older browsers without visualViewport — fall back to 100dvh via
    // the CSS defaults. Nothing to do here.
    return;
  }

  vv.addEventListener('resize', _apply);
  vv.addEventListener('scroll', _apply);
  // Re-apply on orientationchange — some browsers don't fire visualViewport
  // resize promptly on rotation.
  window.addEventListener('orientationchange', _apply);

  _apply();
  _bound = true;
}
